import { unreachableCase } from 'frontend/types';
import { ElmCompiledAsset, LessonAssetTypeName } from 'frontend/types/lessons/editor';

/* In sync with  elmConfigPrelude inside of CodeGen.elm
 *
 */
const ELM_CONFIG_IMPORTS = `
import Interactive exposing (ElmConfigInitializer(..))
import Vector exposing (Vector, v2)
import Html
import RedirectButton.Main
import CryptoOneOffs.App
import Envelope.LegacyNumberFill.App
import Brilliant.Interactive
import Envelope.FillInInteractive.App
`;

function elmConfigHeaderWithModuleName(moduleName: string) {
  return `
module ${moduleName} exposing (..)

${ELM_CONFIG_IMPORTS}
`;
}

// config here comes from the expected API within the elmconfig asset creator

function elmConfigPreviewFooter(elm: string) {
  if (elm.includes('RedirectButtonInteractive')) {
    return `main =
    case config of
        RedirectButtonInteractive subConfig ->
            RedirectButton.Main.program subConfig
        _ ->
            Brilliant.Interactive.unimplementedElmConfigInteractive`;
  }

  if (elm.includes('CryptoOneOffs')) {
    return `main =
    case config of
        CryptoOneOffs subConfig ->
            CryptoOneOffs.App.program subConfig
        _ ->
            Brilliant.Interactive.unimplementedElmConfigInteractive`;
  }

  if (elm.includes('LegacyNumberFill')) {
    return `main =
    case config of
        LegacyNumberFill subConfig ->
            Envelope.LegacyNumberFill.App.program subConfig
        _ ->
            Brilliant.Interactive.unimplementedElmConfigInteractive`;
  }

  if (elm.includes('FillInInteractive')) {
    return `main =
    case config of
        FillInInteractive subConfig ->
            Envelope.FillInInteractive.App.program subConfig
        _ ->
            Brilliant.Interactive.unimplementedElmConfigInteractive`;
  }

  return `main =
    Brilliant.InteractiveProgram.unimplementedElmConfigInteractive`;
}

// TODO: Make this the single canonical location of diagrammar imports etc.
// Generate or pass these values into the following files to remove duplication:
// frontend/elm/lessons/transpiler/BrilliantAST/CodeGen.elm
//    pass DIAGRAMMAR_IMPORTS into transpiler as a flag at initialization time
//    (Elm.BrilliantAST.Compiler.init)
// interactives/packages/diagrammar/templates/Header.elm.template
// interactives/packages/diagrammar/templates/Footer.elm.template
// interactives/packages/diagrammar/templates/SolvableFooter.elm.template
//    Generate template files for use by the V2 diagrammar compiler
//    (brilliant.visualizations.views.DiagrammarCompilationView)

const DIAGRAMMAR_IMPORTS = `
import Bits.Colors
import Diagrammar exposing (..)
import Diagrammar.Animation as Animation
import Diagrammar.Annotations as Annotations
import Diagrammar.Annotations.Angle as Angle exposing (Angle)
import Diagrammar.Annotations.Arrow as Arrow exposing (Arrow)
import Diagrammar.Annotations.Marker as Marker
import Diagrammar.App exposing (InputValues)
import Diagrammar.Color as Color exposing (Color, ColorFamily)
import Diagrammar.Csv as Csv
import Diagrammar.DropShadow as DropShadow exposing (DropShadow)
import Diagrammar.Input as Input exposing (Input)
import Diagrammar.Input.External as External
import Diagrammar.Input.Locator as Locator exposing (Locator)
import Diagrammar.Input.ModeSwitcher as ModeSwitcher exposing (ModeSwitcher)
import Diagrammar.Input.Solvable as Solvable
import Diagrammar.Input.Slider as Slider exposing (Slider)
import Diagrammar.Input.SteppedSlider as SteppedSlider exposing (SteppedSlider)
import Diagrammar.Input.Stepper as Stepper exposing (Stepper)
import Diagrammar.Input.Time as Time exposing (Time)
import Diagrammar.Input.Toggle as Toggle exposing (Toggle)
import Diagrammar.Label as Label exposing (Label)
import Diagrammar.Output as Output exposing (OutputDiagram)
import Diagrammar.Plot2 as Plot2 exposing (Plot2)
import Diagrammar.Plot2.Axis as Axis exposing (Axis)
import Diagrammar.Plot2.Grid as Grid exposing (Grid)
import Diagrammar.Plot2.GridD as GridD
import Diagrammar.Plot2.Legend as Legend exposing (Legend)
import Diagrammar.Plot2.Ticks as Ticks
import Diagrammar.Private.Render
import Diagrammar.Random as Random
import Diagrammar.Sizing as Sizing
import Diagrammar.Stroke as Stroke exposing (Stroke)
import Diagrammar.TextBox as TextBox exposing (TextBox)
import Diagrammar.TextLabel as TextLabel exposing (TextLabel)
import Math exposing (..)
import Misc
import Random
import Range exposing (Range, range)
import String.FloatString as FloatString
import String.IntString as IntString
import Vector exposing (Vector, v2, v3)
`;

function diagrammarHeaderWithModuleName(moduleName: string) {
  return `
module ${moduleName} exposing (..)

import Diagrammar.Embed
${DIAGRAMMAR_IMPORTS}
`;
}

function diagrammarFooter(isSolvable: boolean): string {
  const mainReturnType = isSolvable ? '(Just onCheck)' : 'Nothing';

  return `
main =
  Diagrammar.Embed.interactiveProgram diagramInputs diagram ${mainReturnType}
`;
}

// Must be in sync with genericElmAssetImports in
// frontend/elm/lessons/transpiler/BrilliantAST/CodeGen/Module.elm
function genericElmAssetHeaderWithModuleName(moduleName: string) {
  return `
module ${moduleName} exposing (..)
import Array exposing (Array)
import Bits.Colors
import Dict exposing (Dict)
import Math exposing (..)
import Set exposing (Set)
import Random
import Range exposing (Range, range)
import Vector exposing (Vector, v2, v3)
`;
}

export function userLibImports(userLibs: string[]): string {
  return userLibs
    .map((l: string) => `import BrilliantUniverse.${l}.${l.split('.')[0]} as ${l.split('.')[0]}`)
    .join('\n');
}

function diagrammarHeader(moduleName: string, userLibs: string[] = []): string {
  return `
${diagrammarHeaderWithModuleName(moduleName)}
${userLibImports(userLibs)}
`.trim();
}

function elmconfigHeader(moduleName: string, userLibs: string[] = []): string {
  return `
${elmConfigHeaderWithModuleName(moduleName)}
${userLibImports(userLibs)}
`.trim();
}

function genericElmAssetHeader(moduleName: string, userLibs: string[] = []): string {
  return `
${genericElmAssetHeaderWithModuleName(moduleName)}
${userLibImports(userLibs)}
`.trim();
}

interface CompilableElmArgs {
  // Optional while this is used by legacy code.
  moduleName?: string;
  elm: string;
  userLibs: string[];
  isSolvable: boolean;
  assetType: LessonAssetTypeName;
  alias: string;
}

export function compilableElm(args: CompilableElmArgs): string {
  const { moduleName, elm, userLibs, isSolvable, assetType, alias } = args;

  switch (assetType) {
    case 'elmconfig':
      return `
${elmconfigHeader(moduleName || 'Main', userLibs)}
${elm}
${elmConfigPreviewFooter(elm)}
`.trim();
    case 'genericElmAsset':
      return `
${genericElmAssetHeader(moduleName || 'Main', userLibs)}
${elm}
`.trim();
    case 'diagrammar':
      return `
${diagrammarHeader(moduleName || 'Main', userLibs)}
${elm}
${diagrammarFooter(isSolvable)}
`.trim();
    default:
      throw new Error(`Can't compile asset of type: '${assetType}'`);
  }
}

export function headerLineCount(asset: ElmCompiledAsset): number {
  const { userLibs, type } = asset;
  switch (type) {
    case 'elmconfig':
      return elmconfigHeader('SomeModuleNameJustToCountLines', userLibs).split('\n').length;
    case 'diagrammar':
      return diagrammarHeader('SomeModuleNameJustToCountLines', userLibs).split('\n').length;
    case 'simson': // FALL-THROUGH
    case 'genericElmAsset':
      return genericElmAssetHeader('SomeModuleNameJustToCountLines', userLibs).split('\n').length;
    default:
      unreachableCase(type);
  }
}

export function computeModuleName(authoredName: string, assetIndex: number) {
  // e.g. "Interactive_12_authoredXname"
  return `Interactive_${assetIndex}_${safeElmId(authoredName)}`;
}

const ALPHA_NUMERIC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function safeElmId(authoredName: string) {
  const authNameArray = [...authoredName];
  const safeArray = authNameArray.map(char => {
    if (ALPHA_NUMERIC.includes(char)) {
      return char.toLowerCase();
    }

    if (char === ' ') {
      return '_';
    }

    return 'X';
  });

  return safeArray.join('');
}
